import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Fade,
  Grid,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MainContainer from "../../layout/MainContainer";
import "../../pages/users/Users.scss";
import { useLazyGetPropertyByIdQuery } from "../../services/property";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Property } from "../../types/General";
import { Loader, showError } from "../../constants";
import useTranslation from "../../hooks/Translation";
import { useLanguageContext } from "../../context/Language";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import moment from "moment";
import { formatPhoneLtr } from "../../utils/phoneDisplay";
import DemoToolsPanel from "../../components/DemoToolsPanel";

const PropertyDetails = () => {
  const { id } = useParams();
  const [myImages, setMyImages] = useState<any>([]);
  const navigate = useNavigate();
  const { language } = useLanguageContext() as any;
  const [open_gallery, setOpen_gallery] = useState(false);
  const [propertyDetails, { isLoading }] = useLazyGetPropertyByIdQuery();
  const [propertyData, setPropertyData] = useState<Property>();
  const translate = useTranslation();

  const getLocalizedName = (item?: { name?: string; name_ar?: string }) => {
    if (!item?.name && !item?.name_ar) return "-";
    return language === "ar" ? item?.name_ar || item?.name : item?.name;
  };

  const formatPrice = (price?: string | number) => {
    if (price === undefined || price === null || price === "") return "-";
    const numericPrice = Number(price);
    if (Number.isNaN(numericPrice)) return "-";
    return `${translate?.main?.kd || "KD"} ${numericPrice.toLocaleString("en-US")}`;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : "-";
  };

  const yesNo = (value?: boolean) =>
    value ? translate?.main?.Yes || "Yes" : translate?.main?.No || "No";

  const isAgentCreator = (property?: Property) => {
    if (property?.createdByRole === "agent") return true;
    if (property?.createdByRole === "user") return false;
    return property?.userId?.role === "agent";
  };

  const getCreatorTypeLabel = (property?: Property) => {
    if (property?.createdByAdmin) {
      return translate?.properties?.byAdmin || "By Admin";
    }
    return isAgentCreator(property)
      ? translate?.main?.Agent || "Agent"
      : translate?.main?.User || "User";
  };

  const getOwnerNameLabel = (property?: Property) => {
    return isAgentCreator(property)
      ? (translate?.main?.AgentName || "Agent Name").trim()
      : translate?.refunds?.userName || "User Name";
  };

  /** App-parity listing status labels (MyListingCard). */
  const getListingStatusLabel = (property?: Property) => {
    if (!property) return "-";
    if (property?.isRefundRequested) {
      return translate?.properties?.refundInProgress || "Refund in progress";
    }
    if (property?.status === 3) {
      return translate?.properties?.draft || "Draft";
    }
    if (property?.status === 0) {
      return translate?.properties?.pending || "Pending";
    }
    if (property?.status === 1) {
      return translate?.properties?.published || "Published";
    }
    if (
      property?.status === 4 &&
      (property?.expireStatus === 2 || property?.expireStatus === 7)
    ) {
      return translate?.properties?.rtaTimeout || "RTA timeout";
    }
    if (property?.status === 4) {
      return translate?.properties?.rejected || "Rejected";
    }
    if (property?.status === 5) {
      return translate?.properties?.sold || "Sold";
    }
    return translate?.properties?.expired || "Expired";
  };

  const getPropertyTypeLabel = (type?: number) => {
    if (type === 1) return translate?.properties?.sell || "Sell";
    if (type === 2) return translate?.properties?.rent || "Rent";
    if (type === 3) return translate?.properties?.exchange || "Exchange";
    return "-";
  };

  const getListingTypeLabel = (listingType?: number) => {
    if (listingType === 1) {
      return translate?.properties?.listingNormal || "Normal";
    }
    if (listingType === 2) {
      return translate?.properties?.listingHighlighted || "Highlighted";
    }
    if (listingType === 3) {
      return translate?.properties?.listingAgent || "Agent";
    }
    return "-";
  };

  const getUserDetailById = async (id: string | undefined) => {
    try {
      const response = await propertyDetails({ propertyId: id }).unwrap();
      if (response?.statusCode === 200) {
        setPropertyData(response?.data);
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getUserDetailById(id);
  }, []);

  useEffect(() => {
    if (propertyData) {
      if (propertyData?.image?.length) {
        if (propertyData?.image?.length && myImages?.length === 0) {
          let newObj = propertyData?.image?.map((item: any) => {
            return {
              type: item.endsWith(".mp4") ? "video" : "image",
              file: "File" as any,
              url: resolveMediaUrl(item as string),
            };
          });
          setMyImages([...myImages, ...newObj]);
        }
      }
    }
  }, [propertyData]);

  const renderField = (label: string, value: React.ReactNode) => (
    <Grid item lg={3} md={3} sm={6} xs={12}>
      <Box>
        <Typography component="h5">{label}</Typography>
        <Typography component="p">{value ?? "-"}</Typography>
      </Box>
    </Grid>
  );

  return (
    <MainContainer>
      <Loader isLoad={isLoading} />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">{translate?.properties?.propertyDetails}</h1>
          <Button
            className="btn btn_primary"
            onClick={() => {
              navigate("/manage-properties");
            }}
          >
            {translate?.main?.Back}
          </Button>
        </div>
        <Card className="cards">
          <CardContent sx={{ p: 1 }}>
            <Grid container spacing={2} className="view_box">
              <Grid item xs={10} className="view_box_list">
                <Grid container spacing={3}>
                  {propertyData?.name?.trim() ? (
                    <Grid item lg={3} md={3} sm={6} xs={12}>
                      <Box>
                        <Typography component="h5">
                          {translate?.properties?.customTitle}
                        </Typography>
                        <Typography component="p">
                          {language === "ar"
                            ? propertyData?.name_ar?.trim() || propertyData?.name
                            : propertyData?.name}
                        </Typography>
                      </Box>
                    </Grid>
                  ) : null}

                  {renderField(
                    translate?.main?.Price,
                    formatPrice(propertyData?.price)
                  )}
                  {renderField(
                    translate?.main?.SubCategory,
                    getLocalizedName(propertyData?.subCategoryId)
                  )}
                  {renderField(
                    translate?.main?.PropertyCategory,
                    getLocalizedName(propertyData?.categoryId)
                  )}
                  {renderField(
                    translate?.main?.governorate,
                    getLocalizedName(propertyData?.areaId)
                  )}
                  {renderField(
                    translate?.main?.Location,
                    getLocalizedName(propertyData?.subAreaId)
                  )}
                  {renderField(
                    translate?.main?.address || "Address",
                    propertyData?.address || "-"
                  )}

                  {renderField(
                    translate?.properties?.listingStatus || "Listing Status",
                    getListingStatusLabel(propertyData)
                  )}
                  {renderField(
                    translate?.properties?.blockStatus || "Block Status",
                    propertyData?.isBlocked
                      ? translate?.main?.Inactive
                      : translate?.main?.active
                  )}
                  {renderField(
                    translate?.properties?.propertyType || "Property Type",
                    getPropertyTypeLabel(propertyData?.type)
                  )}
                  {renderField(
                    translate?.properties?.listingType,
                    getListingTypeLabel(propertyData?.listingType)
                  )}
                  {renderField(
                    translate?.properties?.createdBy || "Created By",
                    getCreatorTypeLabel(propertyData)
                  )}
                  {renderField(
                    translate?.properties?.publishedFlag || "Published",
                    yesNo(propertyData?.publish)
                  )}
                  {renderField(
                    translate?.properties?.negotiablePrice || "Negotiable Price",
                    yesNo(propertyData?.negotiablePrice)
                  )}
                  {renderField(
                    translate?.properties?.dontDisturb || "Don't Disturb",
                    yesNo(propertyData?.dontDisturb)
                  )}

                  {renderField(
                    translate?.properties?.views,
                    propertyData?.viewCount ?? 0
                  )}
                  {renderField(
                    translate?.properties?.searchCount || "Search Count",
                    propertyData?.searchCount ?? 0
                  )}

                  {renderField(
                    translate?.main?.Totalareainmetersquare,
                    propertyData?.totalArea
                      ? `${Number(propertyData.totalArea).toLocaleString()} ${translate?.properties?.mtSq}`
                      : "-"
                  )}

                  {renderField(
                    getOwnerNameLabel(propertyData),
                    propertyData?.userId?.fullName || "-"
                  )}
                  {renderField(
                    translate?.properties?.email || "Email",
                    propertyData?.userId?.email || "-"
                  )}
                  {renderField(
                    translate?.properties?.phone || "Phone",
                    propertyData?.userId?.phone
                      ? formatPhoneLtr(
                          propertyData?.userId?.countryCode,
                          propertyData?.userId?.phone
                        )
                      : "-"
                  )}

                  {renderField(
                    translate?.properties?.aboutProperty,
                    propertyData?.description || "-"
                  )}

                  {propertyData?.isRefundRequested !== undefined
                    ? renderField(
                        translate?.properties?.isRefundRequested ||
                          "Refund Requested",
                        yesNo(propertyData?.isRefundRequested)
                      )
                    : null}
                  {propertyData?.isResubmit !== undefined
                    ? renderField(
                        translate?.properties?.isResubmit || "Resubmitted",
                        yesNo(propertyData?.isResubmit)
                      )
                    : null}
                  {propertyData?.expireStatus !== undefined &&
                  propertyData?.expireStatus !== null
                    ? renderField(
                        translate?.properties?.expireStatus || "Expire Status",
                        propertyData?.expireStatus
                      )
                    : null}

                  {renderField(
                    translate?.properties?.createdAt || "Created At",
                    formatDate(propertyData?.createdAt)
                  )}
                  {propertyData?.publishedAt
                    ? renderField(
                        translate?.properties?.publishedAt || "Published At",
                        formatDate(propertyData?.publishedAt)
                      )
                    : null}
                  {propertyData?.highlightedAt
                    ? renderField(
                        translate?.properties?.highlightedAt ||
                          "Highlighted At",
                        formatDate(propertyData?.highlightedAt)
                      )
                    : null}
                  {propertyData?.expiredAt
                    ? renderField(
                        translate?.properties?.expiredAt || "Expired At",
                        formatDate(propertyData?.expiredAt)
                      )
                    : null}
                  {propertyData?.acceptedByAgentAt
                    ? renderField(
                        translate?.properties?.acceptedByAgentAt ||
                          "Accepted By Agent At",
                        formatDate(propertyData?.acceptedByAgentAt)
                      )
                    : null}
                  {propertyData?.saveForLaterAt
                    ? renderField(
                        translate?.properties?.saveForLaterAt ||
                          "Save For Later At",
                        formatDate(propertyData?.saveForLaterAt)
                      )
                    : null}

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Box>
                      <Typography component="h5">
                        {translate?.properties?.mapLink}
                      </Typography>
                      <p
                        style={{
                          textDecoration: "underline",
                          color: "blue",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          window.open(propertyData?.mapLink, "_blank");
                        }}
                      >
                        {propertyData?.mapLink || "-"}
                      </p>
                    </Box>
                  </Grid>

                  {myImages?.length ? (
                    <Grid item xs={12}>
                      <Box>
                        <h3>{translate?.properties?.imagesAndVideos}</h3>
                      </Box>
                      <Box className="other_gallery">
                        <figure>
                          {myImages[0]?.type === "image" ? (
                            <figure>
                              <img src={myImages[0]?.url} alt="upload" />
                            </figure>
                          ) : myImages[0]?.type === "video" ? (
                            <video width="100%" height="100%" controls>
                              <source
                                src={myImages[0]?.url}
                                type="video/mp4"
                              />
                              Your browser does not support the video tag.
                            </video>
                          ) : null}
                          <span
                            className="more"
                            onClick={() => setOpen_gallery(true)}
                          >
                            <img src="/images/icon_image.svg" alt="" />
                            Show all images
                          </span>
                        </figure>
                      </Box>
                    </Grid>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <DemoToolsPanel
          variant="property"
          entityId={id}
          context={{
            listingType: propertyData?.listingType,
            status: propertyData?.status,
            expireStatus: propertyData?.expireStatus,
            isResubmit: propertyData?.isResubmit,
            isFirstTimeExpired: propertyData?.isFirstTimeExpired,
            isSecondTimeExpired: propertyData?.isSecondTimeExpired,
            isRefundRequested: propertyData?.isRefundRequested,
            autoPublishAttempted: propertyData?.autoPublishAttempted,
            publishedAt: propertyData?.publishedAt
              ? formatDate(propertyData.publishedAt)
              : null,
            highlightedAt: propertyData?.highlightedAt
              ? formatDate(propertyData.highlightedAt)
              : null,
          }}
          onSuccess={() => {
            getUserDetailById(id);
          }}
        />
      </div>

      <Modal
        className="modal gallery_modal"
        open={open_gallery}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open_gallery}>
          <Box className="modalBox_body">
            <Box>
              <Button
                className="close_btn"
                onClick={() => setOpen_gallery(false)}
              >
                <CloseIcon />
              </Button>
              <Swiper
                className="location_swiper gallery_swiper"
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={10}
                slidesPerView={1}
                navigation={{
                  nextEl: ".swiper-button-next",
                  prevEl: ".swiper-button-prev",
                }}
                pagination={{
                  el: ".swiper-pagination",
                  dynamicBullets: true,
                  clickable: true,
                }}
                loop
                observer
              >
                <div className="swiper_action">
                  <div className="swiper-button-prev">
                    <ArrowBackIcon />
                  </div>
                  <div className="swiper-pagination"></div>
                  <div className="swiper-button-next">
                    <ArrowForwardIcon />
                  </div>
                </div>
                {myImages?.length
                  ? myImages?.map((item: any, i: any) => (
                      <SwiperSlide key={i}>
                        {item.type === "image" ? (
                          <figure>
                            <img src={item.url} alt="upload" />
                          </figure>
                        ) : item.type === "video" ? (
                          <video controls>
                            <source src={item.url} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        ) : null}
                      </SwiperSlide>
                    ))
                  : null}
              </Swiper>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </MainContainer>
  );
};

export default PropertyDetails;
